import React, { useState } from "react";
import PhotographerFilters from "./PhotographerFilters";
import PhotographerTable   from "./PhotographerTable";
import useRoleAccordion from "./Hooks/useRoleAccordion";
// import useRoleAccordion    from "../hooks/useRoleAccordion";

/**
 * RoleAccordion — generic accordion for any assignable role.
 *
 * Props:
 *  title        string   — accordion heading e.g. "Videographers"
 *  bookingId    string
 *  isOpen       boolean  — modal open state (passed down for effects)
 *  services     array    — for Category filter dropdown
 *  fetchListAPI fn       — paginated list API
 *  fetchAllAPI  fn       — fetch-all for select-all
 *  fetchSortAPI fn|null  — sort/filter API (pass null to hide filter bar)
 *  onSelectionChange fn  — (roleKey, ids) => void  — notifies parent of selections
 *  roleKey      string   — unique key e.g. "videographer"
 */
const RoleAccordion = ({
    title,
    bookingId,
    isOpen,
    services = [],
    fetchListAPI,
    fetchAllAPI,
    fetchSortAPI = null,
    onSelectionChange,
    roleKey,
    videoPackages = [],
    selectedPackageIndex = 0,
    allPackagesSelections = {},
    refreshVersion = 0,
    onAction,
    showFilters = false,
}) => {
    const [accordionOpen, setAccordionOpen] = useState(false);
    const [photographerOverrides, setPhotographerOverrides] = useState({});

    const {
        filteredItems,
        search,
        setSearch,
        selectAllChecked,
        loading,
        isLock,
        page,
        setPage,
        totalPages,
        filters,
        selectedIds,
        LIMIT,
        handleSelectAll,
        handleToggle,
        handleFilterChange,
        resetFilters,
    } = useRoleAccordion({ 
        isOpen, 
        bookingId, 
        fetchListAPI, 
        fetchAllAPI, 
        fetchSortAPI,
        selectedPackageIndex,
        videoPackages,
        roleKey,
        refreshVersion,
        isExpanded: accordionOpen
    });

    const cleanId = (id) => {
        if (!id) return "";
        if (typeof id === "object") return id._id ? id._id.toString() : id.toString();
        return id.toString();
    };

    // 1. Identify IDs assigned in OTHER packages (from existing booking data)
    const otherPackagesAssignedIds = (videoPackages || []).flatMap((pkg, idx) => {
        if (idx === selectedPackageIndex) return [];
        return [
            ...(pkg.assignedEditors || []),
            ...(pkg.assignedPhotographers || [])
        ].map(cleanId).filter(Boolean);
    });

    // 2. Identify IDs currently selected in OTHER packages in the modal
    const otherPackagesSelectedIds = Object.entries(allPackagesSelections).flatMap(([pkgIdx, pkgRoles]) => {
        if (parseInt(pkgIdx) === selectedPackageIndex) return [];
        return Object.values(pkgRoles).flat().map(cleanId).filter(Boolean);
    });

    const otherSelectedIds = [...new Set([...otherPackagesAssignedIds, ...otherPackagesSelectedIds])];

    // 3. Quota calculation
    const currentPkg = videoPackages[selectedPackageIndex];
    const currentPkgType = (currentPkg?.type || "").toLowerCase();
    const lockEditorExpertise =
        roleKey === "editor" &&
        (currentPkgType === "standard" || currentPkgType === "premium");

    // Calculate baseAmount for commission calculation
    const getBaseAmount = () => {
        const packageTotal = currentPkg?.price || 0;
        // const GST_RATE = 0.20;
        return Math.round(packageTotal);
    };
    const baseAmount = getBaseAmount();
    const dbPackageIndex =
        currentPkg?.originalIndices?.[0] ?? selectedPackageIndex;
    const planCategory = currentPkg?.type || currentPkg?.category || "";

    // 4. Check if ANY assigned editor in this package is a studio → plan is fully fulfilled
    const assignedEditorIds = (currentPkg?.assignedEditors || []).map(cleanId);
    const isStudioFulfilled = filteredItems.some(p => (assignedEditorIds.includes(cleanId(p._id)) || p.isAssigned) && p.hasStudio === true);
    const effectiveIsLock = isLock || isStudioFulfilled;

    const visibleSelectedIds = selectedIds.filter(id => filteredItems.some(item => cleanId(item._id) === id));

    // Notify parent whenever selection changes
    React.useEffect(() => {
        const details = visibleSelectedIds.map(id => {
            const p = filteredItems.find(item => cleanId(item._id) === id);
            const commission = photographerOverrides[id]?.commission !== undefined ? photographerOverrides[id].commission : (p?.commissionPercentage ?? 0);
            
            let autoAmount = "";
            const pct = parseFloat(commission);
            const veroCommission = baseAmount * (pct / 100);
            if (p?.gstRegistered) {
                autoAmount = Math.round(baseAmount - veroCommission);
            } else {
                autoAmount = Math.round((baseAmount / 1.18) - veroCommission);
            }

            const amount = photographerOverrides[id]?.amount !== undefined ? photographerOverrides[id].amount : autoAmount;
            return {
                photographerId: id,
                amount: String(amount),
                commission: String(commission),
                packageIndex: dbPackageIndex,
                planCategory: (planCategory || "").toLowerCase(),
                roleKey,
            };
        });
        
        onSelectionChange?.(roleKey, visibleSelectedIds, details);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleSelectedIds.length, photographerOverrides, baseAmount]);

    const handleAction = (actionType) => {
        const photographerDetails = visibleSelectedIds.map(id => {
            const p = filteredItems.find(item => cleanId(item._id) === id);
            const commission = photographerOverrides[id]?.commission !== undefined ? photographerOverrides[id].commission : (p?.commissionPercentage ?? 0);
            
            let autoAmount = "";
            const pct = parseFloat(commission);
            const veroCommission = baseAmount * (pct / 100);
            if (p?.gstRegistered) {
                autoAmount = Math.round(baseAmount - veroCommission);
            } else {
                autoAmount = Math.round((baseAmount / 1.18) - veroCommission);
            }

            const amount = photographerOverrides[id]?.amount !== undefined ? photographerOverrides[id].amount : autoAmount;
            return {
                photographerId: id,
                amount: String(amount),
                commission: String(commission),
                packageIndex: dbPackageIndex,
                planCategory: (planCategory || "").toLowerCase(),
                roleKey,
            };
        });
        onAction?.(roleKey, actionType, photographerDetails);
    };

    return (
        <div className="form-group hourly-role-accordion-item" style={{ gridColumn: "span 2" }}>

            {/* ── Toggle Header ── */}
            <div
                className="accordion-header hourly-role-accordion-header"
                onClick={() => setAccordionOpen((v) => !v)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setAccordionOpen((v) => !v);
                    }
                }}
                aria-expanded={accordionOpen}
            >
                <label>
                    {title}
                    {selectedIds.length > 0 && (
                        <span
                            style={{
                                marginLeft: "8px",
                                backgroundColor: "#3b82f6",
                                color: "#fff",
                                borderRadius: "999px",
                                padding: "2px 8px",
                                fontSize: "12px",
                            }}
                        >
                            {visibleSelectedIds.length} selected
                        </span>
                    )}
                </label>
                <span className="accordion-chevron" aria-hidden="true">
                    {accordionOpen ? "▲" : "▼"}
                </span>
            </div>

            {/* ── Accordion Body ── */}
            {accordionOpen && (
                <div className="photographer-accordion-content">
                    <label style={{ fontWeight: "600", marginBottom: "8px", display: "block" }}>
                        Select {title} for {currentPkg?.type || "this plan"} *
                    </label>

                    {/* Search */}
                    <div className="search-bar-container" style={{ marginBottom: "16px" }}>
                        <input
                            type="text"
                            className="photographer-search-input"
                            placeholder={`Search ${title} by name or phone`}
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(1);
                            }}
                            disabled={effectiveIsLock}
                            style={{
                                width: "100%",
                                padding: "10px 12px",
                                border: "1px solid #dcdcdc",
                                borderRadius: "6px",
                                fontSize: "14px",
                                outline: "none",
                            }}
                        />
                    </div>

                    {/* Max Selection Info */}
                    {(() => {
                        const maxCount = 1;
                        const freelancerAssignedCount = filteredItems.filter(p => (assignedEditorIds.includes(cleanId(p._id)) || p.isAssigned) && p.hasStudio !== true).length;
                        const studioAssignedCount = isStudioFulfilled ? Math.max(0, maxCount - freelancerAssignedCount) : 0;
                        const totalAssigned = freelancerAssignedCount + studioAssignedCount;
                        
                        return (
                            <div style={{ marginBottom: "8px", fontSize: "13px", color: "#6b7280" }}>
                                Plan: <strong>{currentPkg?.type || "Standard"}</strong> | Required: <strong>{maxCount}</strong> | Assigned: <strong>({totalAssigned}/{maxCount})</strong> | Selected: <strong>{visibleSelectedIds.length}</strong> | Studio Assigned: <strong>{studioAssignedCount}</strong> | Freelancers Assigned: <strong>{freelancerAssignedCount}</strong>
                                {isStudioFulfilled && (
                                    <span style={{ color: "#16a34a", marginLeft: "8px", fontWeight: "600" }}>
                                        ✅ Plan fulfilled by Studio
                                    </span>
                                )}
                            </div>
                        );
                    })()}

                    {/* Filters — shown when fetchSortAPI is provided OR showFilters is true */}
                    {(fetchSortAPI || showFilters) && (
                        <PhotographerFilters
                            filters={filters}
                            services={services}
                            isLock={effectiveIsLock}
                            onFilterChange={handleFilterChange}
                            onReset={resetFilters}
                            roleKey={roleKey}
                            expertiseLocked={lockEditorExpertise}
                        />
                    )}

                    {/* Table */}
                    <div className="hourly-photographer-list" style={{ position: "relative", minHeight: loading && filteredItems.length === 0 ? "200px" : "auto" }}>
                        {loading && filteredItems.length === 0 ? (
                            <div className="dropdown-status" style={{ 
                                padding: "40px", 
                                textAlign: "center", 
                                color: "#6b7280" 
                            }}>
                                Loading {title.toLowerCase()}...
                            </div>
                        ) : (
                            <div style={{ position: "relative" }}>
                                {loading && (
                                    <div style={{
                                        position: "absolute",
                                        top: 0, left: 0, right: 0, bottom: 0,
                                        backgroundColor: "rgba(255,255,255,0.4)",
                                        zIndex: 10,
                                        display: "flex",
                                        justifyContent: "center",
                                        paddingTop: "50px",
                                        borderRadius: "8px",
                                        backdropFilter: "blur(1px)"
                                    }}>
                                        <div className="spinner-border text-primary" role="status" style={{ width: "1.5rem", height: "1.5rem" }}>
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                )}
                                <PhotographerTable
                                    photographers={filteredItems.map(p => {
                                        const pId = cleanId(p._id);
                                        const assignedIds = (currentPkg?.assignedEditors || []).map(cleanId);
                                        const invitedIds = (currentPkg?.invitedEditors || []).map(cleanId);
                                        return {
                                            ...p,
                                            isAssigned: p.isAssigned || assignedIds.includes(pId),
                                            isInvited: p.isInvited || invitedIds.includes(pId)
                                        };
                                    })}
                                    page={page}
                                    totalPages={totalPages}
                                    LIMIT={LIMIT}
                                    isLock={effectiveIsLock}
                                    selectAllChecked={selectAllChecked}
                                    selectedIds={selectedIds}
                                    otherSelectedIds={otherSelectedIds}
                                    onSelectAll={(e) => {
                                        const excluded = [...otherSelectedIds, ...(currentPkg?.assignedEditors || []).map(cleanId)];
                                        handleSelectAll(e, Infinity, excluded);
                                    }}
                                    onToggle={handleToggle}
                                    onPageChange={setPage}
                                    baseAmount={baseAmount}
                                    maxCount={1}
                                    photographerOverrides={photographerOverrides}
                                    setPhotographerOverrides={setPhotographerOverrides}
                                />
                            </div>
                        )}
                    </div>

                    {/* ── Accordion Actions ── */}
                    {(() => {
                        const hasSelection = selectedIds.length > 0;
                        return (
                            <div className="accordion-actions" style={{ 
                                display: "flex", 
                                justifyContent: "flex-end",
                                gap: "12px", 
                                marginTop: "24px",
                                paddingTop: "20px",
                                borderTop: "1px solid #f3f4f6" 
                            }}>
                                <button
                                    className="btn-request"
                                    disabled={effectiveIsLock || (!selectAllChecked && selectedIds.length === 0)}
                                    type="button"
                                    onClick={() => handleAction('request')}
                                    style={{
                                        width: "180px",
                                        padding: "10px 16px",
                                        borderRadius: "8px",
                                        border: "none",
                                        backgroundColor: (selectAllChecked || hasSelection) ? "#4a3326" : "#f3f4f6",
                                        color: (selectAllChecked || hasSelection) ? "white" : "#9ca3af",
                                        fontWeight: "700",
                                        fontSize: "12px",
                                        letterSpacing: "0.5px",
                                        textTransform: "uppercase",
                                        cursor: (selectAllChecked || hasSelection) ? "pointer" : "not-allowed",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "8px",
                                        boxShadow: (selectAllChecked || hasSelection) ? "0 4px 6px -1px rgba(10, 88, 202, 0.2)" : "none",
                                        transition: "all 0.3s ease",
                                    }}
                                >
                                     Send Request
                                </button>
                            </div>
                        );
                    })()}
                </div>
            )}
        </div>
    );
};

export default RoleAccordion;